/**
 * BM3 Deprecation Phase 2 — gated wrapper around the raw BM3 page-segment-load factory.
 *
 * This file is the canonical implementation behind the public name
 * `createPageSegmentLoadMetric` (re-exported from
 * `@confluence/browser-metrics/entry-points/metricConfigs.ts`). All Confluence consumers
 * call the gated variant by virtue of importing the public name; there is no separate
 * "raw" public entry point.
 *
 * When the `cc_bm3_deprecation_phase_2` gate is OFF (default), every method call delegates
 * verbatim to the underlying BM3 PageSegmentLoadMetric so existing behaviour and the BM3
 * `PAGE_SEGMENT_LOAD` analytics events are preserved.
 *
 * When the gate is ON, the BM3 calls are skipped and equivalent UFO custom marks / custom
 * data are emitted instead. This lets us roll out the removal of the BM3 page-segment-load
 * events incrementally — controlled by Statsig — without touching any of the ~25
 * perf.config files or ~20 imperative call sites. Consumers continue to use
 * `METRIC.start()`, `.stop()`, `.cancel()`, `.mark()`, `.markFMP()`,
 * `.startFromPageLoad()` exactly as before; the gate decision is centralised here.
 *
 * The gate `fg(...)` is evaluated **inline at the callsite of each method**, never aliased
 * or wrapped, in line with the feature-gate-and-experiment-usage AGENTS guidance. Per-method
 * (rather than per-construction) evaluation is required because perf.config modules are
 * imported very early in app boot — earlier than Statsig data is loaded — so a one-shot
 * `fg()` call at construction time would always read the default (false) value.
 *
 * Implementation note: rather than returning a *new* wrapper object, this helper mutates
 * the real BM3 metric instance by overriding selected methods with bound functions that
 * consult the gate. This preserves the metric's class identity, prototype chain, and
 * private internal state so it remains a fully drop-in substitute everywhere a BM3
 * PageSegmentLoadMetric is used (including in `pageLoadMetric.include[]` / `until[]`
 * arrays where BM3 walks the internal experience graph).
 *
 * Once the gate has fully ramped, this file's body will be replaced with a UFO-only
 * implementation and the BM3 dependency removed entirely.
 */
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { addUFOCustomMark } from '@atlaskit/react-ufo/custom-mark';

import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/page-segment-load';

import { fg } from '@confluence/feature-gating';

import {
	PAGE_SEGMENT_MARK_PREFIX_END,
	PAGE_SEGMENT_MARK_PREFIX_START,
} from './metric-keys/index.ts';
import { createBM3PageSegmentLoadMetricRaw } from './createBM3PageSegmentLoadMetricRaw.ts';

type CreateConfig = Parameters<typeof createBM3PageSegmentLoadMetricRaw>[0];

/**
 * Creates a page-segment-load metric. The returned object is the underlying BM3 metric
 * instance with selected methods overridden to gate the BM3 calls behind
 * `cc_bm3_deprecation_phase_2`. See file header for rationale.
 */
export function createPageSegmentLoadMetric(config: CreateConfig): PageSegmentLoadMetric {
	const metric = createBM3PageSegmentLoadMetricRaw(config);
	const key = config.key;

	// Capture the original methods before overriding them.
	const realStart = metric.start.bind(metric);
	const realStartFromPageLoad = metric.startFromPageLoad.bind(metric);
	const realStop = metric.stop.bind(metric);
	const realCancel = metric.cancel.bind(metric);
	const realMark = metric.mark.bind(metric);
	const realMarkFMP = metric.markFMP.bind(metric);

	metric.start = (...args: Parameters<PageSegmentLoadMetric['start']>) => {
		if (fg('cc_bm3_deprecation_phase_2')) {
			addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_START}:${key}`);
			return;
		}
		return realStart(...args);
	};

	metric.startFromPageLoad = (...args: Parameters<PageSegmentLoadMetric['startFromPageLoad']>) => {
		if (fg('cc_bm3_deprecation_phase_2')) {
			addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_START}:${key}`);
			return;
		}
		return realStartFromPageLoad(...args);
	};

	metric.stop = (...args: Parameters<PageSegmentLoadMetric['stop']>) => {
		if (fg('cc_bm3_deprecation_phase_2')) {
			addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_END}:${key}`);
			const opts = args[0];
			if (opts?.customData) {
				addUFOCustomData(opts.customData);
			}
			return true;
		}
		return realStop(...args);
	};

	metric.cancel = (...args: Parameters<PageSegmentLoadMetric['cancel']>) => {
		if (fg('cc_bm3_deprecation_phase_2')) {
			// Note - there's no equivalent for cancel in UFO, so we use the end mark instead.
			addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_END}:${key}`);
			return;
		}
		return realCancel(...args);
	};

	metric.mark = (...args: Parameters<PageSegmentLoadMetric['mark']>) => {
		if (fg('cc_bm3_deprecation_phase_2')) {
			const [name, timestamp] = args;
			addUFOCustomMark(name, timestamp);
			return;
		}
		return realMark(...args);
	};

	metric.markFMP = (...args: Parameters<PageSegmentLoadMetric['markFMP']>) => {
		if (fg('cc_bm3_deprecation_phase_2')) {
			const [timestamp] = args;
			addUFOCustomMark(`fmp:${key}`, timestamp);
			return;
		}
		return realMarkFMP(...args);
	};

	return metric;
}
